/**
 * WorkOrderAccordion — shared WO sub-row component used by:
 *   - JobsPage (table-row context, colSpan wrapper)
 *   - JobDetail WorkOrdersTab (standalone card context)
 *
 * GP-07 / PR-7: extracted from JobsPage to avoid two diverging implementations.
 *
 * PR-72 Commit 1 (UAT 08-19 :19 stakeholder ask; operator ruling 08-22):
 *   Column headers added ABOVE the existing cards. Not a Table-component
 *   conversion — the cards keep their richer styling. Not counted toward
 *   PR-70's table-adoption census. Header order mirrors the Jobs landing
 *   column order (identity → who → state → when) per 00_OPERATOR_RULINGS:499,
 *   "one mental model wherever a WO row appears." Label "Scheduled" (not
 *   "Scheduled Date") survives PORTICA-76 include-weekends turning that
 *   value into a range.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/20/solid';
import { Button } from '../ui/Button';
import StatusPill from '../ui/StatusPill';
import SalespeopleChips from '../ui/SalespeopleChips';
import DropdownMenu from '../ui/DropdownMenu';
import { useSettingsContext } from '../../hooks/useSettings';
import { woSchedule } from '../../utils/woSchedule';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { crewIdentity } from '../../utils/crewIdentity';
import useSettingsList from '../../hooks/useSettingsList';
import { woRowHeading } from '../../utils/workOrderEdit';

/**
 * Single WO row — the shared visual unit.
 */
// IDI-475: a WO link names its job, so the page never has to guess.
const woHref = (woId, jobId) => `/work-orders/${encodeURIComponent(woId)}${jobId ? `?job=${encodeURIComponent(jobId)}` : ''}`;

function WORow({ wo, jobId, crews = [], onDeleteWO }) {
  const navigate = useNavigate();
  const { getEnumLabel } = useSettingsContext();
  const status = (wo.status || '').toLowerCase();
  const heading = woRowHeading(wo, getEnumLabel);

  // IDI-472 (supersedes the PR-14 order): the crew's LIVE Settings colour
  // and name win; the stamped snapshot is used only when the crew is gone.
  // Unassigned shows the border.
  const crew = crewIdentity(wo, crews);
  const crewBarColor = crew.assigned ? crew.color : 'var(--color-border)';

  return (
    <div
      className="flex items-center gap-4 px-3 py-2 border-b border-[color:var(--color-border)] last:border-b-0 hover:bg-gray-50 cursor-pointer"
      onClick={() => navigate(woHref(wo.id, jobId), { state: { jobId } })}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(woHref(wo.id, jobId), { state: { jobId } }); }}
    >
      <div
        className="w-1 h-8 rounded-full flex-shrink-0"
        style={{ backgroundColor: crewBarColor }}
      />
      <div className="w-2/5 min-w-0">
        {/* IDI-470: the typed title leads; type \u00b7 number beneath; never a raw id. */}
        <p className="text-sm font-medium text-gray-900 truncate">{heading.title}</p>
        {heading.meta && <p className="text-xs text-gray-500 truncate">{heading.meta}</p>}
      </div>
      <div className="w-1/5 min-w-0">
        {(() => {
          const techs = wo.techs || [];
          if (crew.assigned && crew.name) return <SalespeopleChips salespeople={[{id: crew.crewId || "crew", name: crew.name}]} interactive={false} color="neutral" swatch={crewBarColor} />;
          if (techs.length > 0) return <SalespeopleChips salespeople={techs} interactive={false} color="neutral" />;
          return "\u2014";
        })()}
      </div>
      <div className="w-1/5"><StatusPill status={status} /></div>
      <div className="w-1/5 text-sm text-gray-700">
        {(() => {
          // IDI-185 · stacked Start/Stop lines. Two lines, one column.
          // Operator override on legacy's two-column shape: width w-1/5 is
          // too narrow for two "MM/DD/YYYY H:MM AM/PM" cells side-by-side
          // (measured: ~18 chars each; two columns truncate both). Stack
          // vertically at the same width for readable size.
          //
          // Composition rules (IDI-185):
          //   · getScheduledDate handles the date alias chain (R-90);
          //     getScheduledStartTime / getScheduledEndTime handle the time
          //     alias chain (IDI-185). N-14/D-235: alias handling lives in
          //     canonicalFields.js, never inline in JSX.
          //   · Take the DATE PART of scheduledDate first: split('T')[0].
          //     scheduledDate is EITHER "YYYY-MM-DD" (modern, 1/443 on
          //     tenant 270) OR a full ISO timestamp (legacy, 442/443).
          //     Composing an ISO timestamp with a separate HH:mm produces
          //     garbage.
          //   · Route through formatDate / formatDateTime — the shared
          //     utility (utils/formatDate.js). Any second implementation
          //     of date formatting is a defect per that file's own rule.
          //     F5 catches shadow formatters via a git grep.
          //   · Time absent → formatDate(scheduledDate), date only (Q3).
          //   · Date absent → em-dash (F4). Never a bare dash next to a
          //     real date; never an empty cell.
          //
          // IDI-259 owns the calendar-date TZ shift for bare YYYY-MM-DD.
          // toDate() at formatDate.js:44 special-cases that; do not
          // reintroduce the naive `new Date(bareDate)` here.
          // IDI-471: one reader (utils/woSchedule) for start AND stop. The
          // stop now carries its OWN date (endDate / stopAt); it used to be
          // built from the start date, so a multi-day WO showed one day.
          // A stop still renders only when the record has one (F6/F7).
          const sched = woSchedule(wo);
          if (!sched.start) return "\u2014";
          const startCell = sched.hasStartTime
            ? formatDateTime(`${sched.startDate}T${sched.startTime}`)
            : formatDate(sched.startDate);
          const stopCell = !sched.stop
            ? null
            : sched.hasStopTime
              ? formatDateTime(`${sched.stopDate}T${sched.stopTime}`)
              : (sched.multiDay ? formatDate(sched.stopDate) : null);

          return (
            <div className="space-y-0.5">
              <div data-testid="wo-row-start">{startCell}</div>
              {stopCell && (
                <div data-testid="wo-row-stop" className="text-gray-500">{stopCell}</div>
              )}
            </div>
          );
        })()}
      </div>
      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu
          ariaLabel={`Actions for ${heading.title}`}
          items={[
            // IDI-467 (D-01): no manual Complete. WOs complete at their stop.
            { label: 'View work order', onClick: () => navigate(woHref(wo.id, jobId), { state: { jobId } }) },
            // IDI-469: only JobDetail's Work Orders tab passes onDeleteWO.
            ...(onDeleteWO ? [{ label: 'Delete work order', onClick: () => onDeleteWO(wo), variant: 'danger' }] : []),
          ]}
        />
      </div>
    </div>
  );
}

/**
 * WorkOrderAccordion — renders a list of WO rows.
 * Wrapper is left to the consumer (table cell or standalone div).
 */
export default function WorkOrderAccordion({ workOrders, jobId, onCreateWO, emptyMessage, crews, onDeleteWO }) {
  // IDI-472: one crew list for every row (live names + colours). A caller that
  // already has the list passes it; otherwise it's read once here, not per row.
  const { items: loadedCrews } = useSettingsList(crews ? null : 'crews');
  const crewList = crews || loadedCrews;

  if (!workOrders || workOrders.length === 0) {
    return (
      <div className="flex items-center justify-between py-3">
        <p className="text-sm text-gray-500">{emptyMessage || 'No work orders scheduled'}</p>
        {onCreateWO && (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onCreateWO(); }}
          >
            <PlusIcon className="h-3 w-3 mr-1" />
            Create Work Order
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* PR-72 Commit 1: visual column headers over the existing cards.
          Operator ruling 08-22: headers-over-cards, NOT a Table conversion —
          so this markup MUST NOT claim table semantics. No role="table",
          no role="row", no role="columnheader" — those would be orphans
          (data rows below carry role="button", not role="row") and would
          also re-open the PR-70 table-adoption census question the ruling
          was written to avoid. Each card is self-describing to a screen
          reader; the header strip is decorative labelling and is marked
          aria-hidden.

          Widths mirror the WORow layout exactly: gap-4, px-3, w-1 bar →
          w-2/5 → w-1/5 × 3 → w-8 actions. Colour-bar and actions columns
          are headerless, matching Jobs landing's unlabelled checkbox and
          per-row actions.

          data-testid is the query hook for the order-sensitive red-proof
          test — do not remove without updating
          WorkOrderAccordion.headers.test.js. */}
      <div
        className="flex items-center gap-4 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b border-[color:var(--color-border)]"
        data-testid="wo-accordion-headers"
        aria-hidden="true"
      >
        <div className="w-1 flex-shrink-0" />
        <div className="w-2/5">Work Order</div>
        <div className="w-1/5">Crew</div>
        <div className="w-1/5">Status</div>
        <div className="w-1/5">Scheduled</div>
        <div className="w-8 flex-shrink-0" />
      </div>
      {workOrders.map((wo) => (
        <WORow key={wo.id} wo={wo} jobId={jobId} crews={crewList} onDeleteWO={onDeleteWO} />
      ))}
    </div>
  );
}

export { WORow };
